import { HttpService, LocalContext, Repository, RepositoryBase, TaggedUrl, Tags, Weaver } from '@skylab-shared/core';
import { DocumentPrintingPatternModel, PrintingPatternGridColumnDefinition, PrintingPatternGridDefinition, PrintingPatternModel } from '@skylab-shared/dataModel';
import { pickBy, ResponseUtils } from '@skylab-shared/utils';
import { FlagChangeResponse } from '../common/flag-change-response';
import { PrintingPatternBO } from './printing-pattern.bo';


export interface PrintingAvailableColumn {
    order?: number;
    width: number;
    name: string;
    id: string;
}

export interface PrintingDictionary {
    id: number;
    name: string;
    language: string;
}

@Repository({
    boType: PrintingPatternBO,
    apiController: TaggedUrl.join('documents/printingTemplates', [Tags.Api])
})
export class PrintingPatternRepositoryBase extends RepositoryBase<PrintingPatternBO, PrintingPatternModel> {
    public constructor(
        private readonly weaver: Weaver,
        $http: HttpService
    ) {
        super($http);
    }

    protected createBO(): PrintingPatternBO {
        return new PrintingPatternBO(this.weaver);
    }

    protected async fromPlainObjectAsync(item: any): Promise<PrintingPatternModel> {
        const itemsList = new PrintingPatternGridDefinition();
        if (item.itemsList && item.itemsList.columns) {
            item.itemsList.columns.forEach((c: any) => {
                itemsList.columns.push(new PrintingPatternGridColumnDefinition({
                    id: c.id,
                    width: c.width
                }));
            });
        }
        const model = new PrintingPatternModel(pickBy({
            id: item.id,
            version: item.version,

            default: item.default,
            disableShadows: item.disableShadows,
            documentTitle: item.documentTitle,
            finalAdvanceDocumentTitle: item.finalAdvanceDocumentTitle,
            itemsList: itemsList,
            kind: item.kind,
            name: item.name,
            note: item.note,
            secondaryLanguage: item.secondaryLanguage,
            myCompanyNote: item.myCompanyNote,
            includes: item.includes ? new DocumentPrintingPatternModel(pickBy({
                baseCurrencyDocumentGross: item.includes.baseCurrencyDocumentGross,
                baseCurrencyVatTable: item.includes.baseCurrencyVatTable,
                vatTableWhenReverseCharge: item.includes.vatTableWhenReverseCharge,
                vatTable: item.includes.vatTable,
                deliveryAddress: item.includes.deliveryAddress,
                documentNote: item.includes.documentNote,
                exchangeRate: item.includes.exchangeRate,
                explainSaleDate: item.includes.explainSaleDate,
                issuer: item.includes.issuer,
                itemNote: item.includes.itemNote,
                leftToPay: item.includes.leftToPay,
                logo: item.includes.logo,
                productNote: item.includes.productNote,
                recipient: item.includes.recipient,
                relatedClientTIN: item.includes.relatedClientTIN,
                sourceDocumentInfo: item.includes.sourceDocumentInfo,
                jpkCodes: item.includes.jpkCodes,
                unsettledDocuments: item.includes.unsettledDocuments,
                previousAdvancesList: item.includes.previousAdvancesList,
                payments: item.includes.payments,
                ksefNumber: item.includes.ksefNumber,
                summary: item.includes.summary
            }, val => val !== void 0)) : null,
        }, val => val !== void 0));
        return model;
    }

    protected async preparePlainObjectAsync(model: PrintingPatternModel, context: LocalContext): Promise<any> {
        const columns: any[] = [];
        model.itemsList.columns.forEach(function (item) {
            columns.push({
                id: item.id,
                width: item.width
            });
        });

        return {
            id: model.id,
            default: model.default,
            disableShadows: model.disableShadows,
            documentTitle: model.documentTitle,
            finalAdvanceDocumentTitle: model.finalAdvanceDocumentTitle,
            includes: {
                baseCurrencyDocumentGross: model.includes.baseCurrencyDocumentGross,
                baseCurrencyVatTable: model.includes.baseCurrencyVatTable,
                vatTable: model.includes.vatTable,
                vatTableWhenReverseCharge: model.includes.vatTableWhenReverseCharge,
                deliveryAddress: model.includes.deliveryAddress,
                documentNote: model.includes.documentNote,
                exchangeRate: model.includes.exchangeRate,
                explainSaleDate: model.includes.explainSaleDate,
                issuer: model.includes.issuer,
                itemNote: model.includes.itemNote,
                leftToPay: model.includes.leftToPay,
                logo: model.includes.logo,
                productNote: model.includes.productNote,
                recipient: model.includes.recipient,
                relatedClientTIN: model.includes.relatedClientTIN,
                sourceDocumentInfo: model.includes.sourceDocumentInfo,
                jpkCodes: model.includes.jpkCodes,
                unsettledDocuments: model.includes.unsettledDocuments,
                previousAdvancesList: model.includes.previousAdvancesList,
                payments: model.includes.payments,
                ksefNumber: model.includes.ksefNumber,
                summary: model.includes.summary
            },
            itemsList: { columns: columns },
            kind: model.kind,
            myCompanyNote: model.myCompanyNote,
            name: model.name,
            note: model.note,
            secondaryLanguage: model.secondaryLanguage,
            version: model.version
        };
    }

    //#region default
    public async setAsDefaultAsync(patternType: string, id: number): Promise<FlagChangeResponse | undefined> {
        try {
            const response = await this.sendRequest<{ status: number; data: { message: string; object: { id: number; version: string } } }>(
                'PUT',
                `[api] documents/printingTemplates/${patternType}/default`,
                { params: { id: id } }
            );
            if (ResponseUtils.isStatusSuccess(response.status)) {
                const successData = response.data;
                return {
                    value: true,
                    id: successData.object.id,
                    version: successData.object.version
                };
            }
        } catch (err) {
            this.logger.error(err);
        }

        return undefined
    }
    //#endregion
}
